import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import NotificationBell from '../components/NotificationBell';
import DashboardAMO from '../components/dashboards/DashboardAMO';
import DashboardIntervenant from '../components/dashboards/DashboardIntervenant';
import DashboardClient from '../components/dashboards/DashboardClient';
import DashboardEntreprise from '../components/dashboards/DashboardEntreprise';
import MaSemaineCabinet from '../components/dashboards/MaSemaineCabinet';
import { URL_MA_SEMAINE, dateCourte, heureCourte, lienChantier } from '../components/dashboards/maSemaine';
import { isEntrepriseRole } from '../lib/membership';
import { 
  Folder, 
  Clock, 
  CheckCircle, 
  Compass,
  CalendarBlank,
  ArrowRight,
  Plus
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ETAPES_CHANTIER, ETAPES_KANBAN, etapeChantier, libelleEtape, STATUT_CHANTIER } from '../lib/libelles';
import CartePremierJour from '../components/premier-jour/CartePremierJour';
import DossiersReprisCard from '../components/dashboards/DossiersReprisCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B1 — colonnes du Kanban = l'étape unique du chantier (lue sur le serveur) :
// Études · Permis · Consultation · Chantier · Réception · GPA ; « Clôturé » est rangé à part.
const etapeColors = {
  etudes: 'phase-esq',
  permis: 'phase-aps',
  consultation: 'phase-dce',
  chantier: 'phase-exe',
  reception: 'phase-opr',
  gpa: 'phase-aor',
  cloture: 'badge-termine',
};

export default function Dashboard() {
  const { user } = useAuth();

  // Role-specific dashboards
  if (user?.role === 'amo') return <Layout><div className="p-6"><NotificationBell /><DashboardAMO user={user} /></div></Layout>;
  // PLA-02 — partenaires (bet, consultant) : même tableau de bord que « Intervenant »
  // (avant : tableau de bord architecte) ; salarié d'entreprise : celui de l'entreprise.
  if (['intervenant', 'bet', 'consultant'].includes(user?.role)) return <Layout><div className="p-6"><DashboardIntervenant user={user} /></div></Layout>;
  if (user?.role === 'client') return <Layout><div className="p-6"><DashboardClient user={user} /></div></Layout>;
  if (isEntrepriseRole(user?.role)) return <Layout><div className="p-6"><DashboardEntreprise user={user} /></div></Layout>;

  // Admin / Architect dashboard
  return <AdminArchitectDashboard />;
}

function AdminArchitectDashboard() {
  const { user, canEdit } = useAuth();
  const [stats, setStats] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [chantierBadges, setChantierBadges] = useState({});
  // RP80-81 — « Ma semaine » : actions en retard, levées, CR à relire, réunions, visas
  const [semaine, setSemaine] = useState(null);
  const [semaineLoading, setSemaineLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}${URL_MA_SEMAINE}`, { withCredentials: true })
      .then((r) => setSemaine(r.data))
      .catch(() => setSemaine(null))
      .finally(() => setSemaineLoading(false));
  }, []);

  useEffect(() => {
    // M2 Assistant — Badges "X nouveaux éléments" sur chaque chantier
    axios.get(`${API_URL}/api/dashboard/since-last-crc-summary`, { withCredentials: true })
      .then((r) => {
        const map = {};
        (r.data?.summaries || []).forEach((s) => { map[s.chantier_id] = s.total; });
        setChantierBadges(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, chantiersRes] = await Promise.all([
          axios.get(`${API_URL}/api/stats/dashboard`, { withCredentials: true }),
          axios.get(`${API_URL}/api/chantiers`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setChantiers(chantiersRes.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Kanban : un chantier par étape ; les chantiers clôturés sont rangés à part (P5#16)
  const groupedByEtape = ETAPES_KANBAN.map((e) => ({
    ...e,
    items: chantiers.filter((c) => etapeChantier(c) === e.cle),
  }));
  // Fluidité B4 + B1 — les chantiers clôturés sortent des colonnes actives (rangés à part)
  const clotures = chantiers.filter((c) => c.cloture_le || etapeChantier(c) === 'cloture');
  const [voirClotures, setVoirClotures] = useState(false);

  const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
    <div className="card-kroquis flex items-start gap-4" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className={`w-12 h-12 flex items-center justify-center ${color}`}>
        <Icon size={24} weight="bold" className="text-white" />
      </div>
      <div>
        <p className="text-3xl font-bold tracking-tight text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
          {value}
        </p>
        <p className="text-sm text-[#6B6B6B]">{label}</p>
        {subtext && <p className="text-xs text-[#9A9A9A] mt-1">{subtext}</p>}
      </div>
    </div>
  );

  const KanbanCard = ({ chantier }) => (
    <Link
      to={`/chantiers/${chantier.id}`}
      className="kanban-card block"
      data-testid={`kanban-card-${chantier.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        {/* P1#25 — nom complet (plus de « Maison… » tronqué) */}
        <h4 className="font-medium text-[#2C2C2C] text-sm flex-1 break-words leading-snug" title={chantier.name}>{chantier.name}</h4>
        {chantierBadges[chantier.id] > 0 && (
          <span
            className="text-[9px] px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded font-semibold flex-shrink-0"
            data-testid={`dashboard-badge-new-${chantier.id}`}
          >
            +{chantierBadges[chantier.id]}
          </span>
        )}
      </div>
      <p className="text-xs text-[#6B6B6B] truncate mb-2">{chantier.address}</p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-[#9A9A9A]">{chantier.owner_name}</span>
        <span className="flex items-center gap-1">
          {['prospection', 'en_pause'].includes(chantier.status) && (
            <span className="text-[10px] font-medium text-[#6B6B6B] border border-[#E0E0DB] px-1.5 py-0.5">{STATUT_CHANTIER[chantier.status]}</span>
          )}
          {chantier.current_phase && ETAPES_CHANTIER.find((e) => e.cle === etapeChantier(chantier))?.phases.includes(String(chantier.current_phase).toUpperCase()) && (
            <span className="text-xs font-medium text-[#1E3A5F] bg-[#E3F2FD] px-1.5 py-0.5" title="Phase (détail)">{chantier.current_phase}</span>
          )}
        </span>
      </div>
    </Link>
  );

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Bonjour, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-[#6B6B6B] mt-1">
              Voici l'état de vos chantiers aujourd'hui
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <NotificationBell />
            
            {canEdit && (
              <Link to="/chantiers/new">
                <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" data-testid="new-chantier-button">
                  <Plus size={18} weight="bold" />
                  Nouveau chantier
                </Button>
              </Link>
            )}
          </div>
        </div>

        <CartePremierJour />{/* R7 / RP92 — « Terminer la configuration » (admin seulement) */}
        <DossiersReprisCard />{/* Fluidité B4 (P5#22) — « Dossiers repris de … » après un départ */}

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Folder} label="Chantiers" value={stats?.total_chantiers || 0} color="bg-[#2C2C2C]" />
            <StatCard icon={Clock} label="En cours" value={stats?.en_cours || 0} color="bg-[#C75B39]" subtext="De la conception à la GPA" />
            <StatCard icon={CheckCircle} label="Clôturés" value={stats?.termine || 0} color="bg-[#2D6A4F]" />
            <StatCard icon={Compass} label="Prospection" value={stats?.prospection || 0} color="bg-[#1E3A5F]" />
          </div>
        )}

        {/* RP80-81 — Ma semaine (par sujet / par chantier) */}
        <MaSemaineCabinet semaine={semaine} loading={semaineLoading} />

        {/* View Toggle */}
        <div className="mb-6">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="bg-[#F5F5F3] rounded-none p-1 h-auto">
              <TabsTrigger value="kanban" className="rounded-none data-[state=active]:bg-white text-sm">
                Vue Kanban
              </TabsTrigger>
              <TabsTrigger value="list" className="rounded-none data-[state=active]:bg-white text-sm">
                Vue Liste
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Kanban / List View */}
          <div className="lg:col-span-3">
            {view === 'kanban' ? (
              <>
              <div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-6 gap-4">
                {groupedByEtape.map(({ cle, libelle, items }) => (
                  <div key={cle} className="kanban-column p-4" data-testid={`kanban-col-${cle}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className={`text-xs px-2 py-1 font-bold uppercase tracking-wider ${etapeColors[cle]}`}>
                        {libelle}
                      </span>
                      <span className="text-xs text-[#6B6B6B]">{items.length}</span>
                    </div>
                    <div className="space-y-3">
                      {items.length === 0 ? (
                        <p className="text-sm text-[#9A9A9A] text-center py-8">Aucun chantier</p>
                      ) : (
                        items.map(chantier => (
                          <KanbanCard key={chantier.id} chantier={chantier} />
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* P5#16 — les chantiers clôturés ne restent plus dans la colonne « Chantier » */}
              {clotures.length > 0 && (
                <div className="mt-4 border border-[#E0E0DB] bg-white" data-testid="kanban-clotures">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                    onClick={() => setVoirClotures(!voirClotures)}
                  >
                    <span className="text-sm font-medium text-[#2C2C2C]">Chantiers clôturés ({clotures.length})</span>
                    <span className="text-xs text-[#C75B39] font-medium">{voirClotures ? 'Masquer' : 'Afficher'}</span>
                  </button>
                  {voirClotures && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-4 pb-4">
                      {clotures.map((chantier) => <KanbanCard key={chantier.id} chantier={chantier} />)}
                    </div>
                  )}
                </div>
              )}
              </>
            ) : (
              <div className="card-kroquis">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    Tous les chantiers
                  </h2>
                  <Link to="/chantiers" className="text-[#C75B39] text-sm font-medium flex items-center gap-1 hover:underline">
                    Voir tout <ArrowRight size={16} />
                  </Link>
                </div>
                
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16" />
                    ))}
                  </div>
                ) : chantiers.length === 0 ? (
                  <div className="text-center py-12">
                    <Folder size={48} className="mx-auto text-[#E0E0DB] mb-4" />
                    <p className="text-[#6B6B6B]">Aucun chantier pour le moment</p>
                    {canEdit && (
                      <Link to="/chantiers/new">
                        <Button className="mt-4 bg-[#C75B39] hover:bg-[#D97B5C] rounded-none">
                          Créer votre premier chantier
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full table-kroquis">
                      <thead>
                        <tr>
                          <th className="text-left">Chantier</th>
                          <th className="text-left">Client</th>
                          <th className="text-left">Étape</th>
                          <th className="text-left">Phase</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chantiers.slice(0, 8).map(chantier => (
                          <tr key={chantier.id}>
                            <td>
                              <Link to={`/chantiers/${chantier.id}`} className="font-medium text-[#2C2C2C] hover:text-[#C75B39]">
                                {chantier.name}
                              </Link>
                              <p className="text-xs text-[#9A9A9A]">{chantier.address}</p>
                            </td>
                            <td className="text-[#4A4A4A]">{chantier.owner_name}</td>
                            <td>
                              <span className={`text-xs px-2 py-1 uppercase tracking-wider font-bold ${etapeColors[etapeChantier(chantier)] || ''}`}>
                                {chantier.etape_libelle || libelleEtape(etapeChantier(chantier))}
                              </span>
                              {['prospection', 'en_pause'].includes(chantier.status) && (
                                <span className="ml-2 text-[10px] text-[#6B6B6B]">{STATUT_CHANTIER[chantier.status]}</span>
                              )}
                            </td>
                            <td className="text-sm text-[#1E3A5F]">{chantier.current_phase || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Réunions de la semaine (evenements, RP80) — tous les chantiers de « Ma semaine » */}
            <div className="card-kroquis" data-testid="dashboard-reunions">
              <h3 className="text-base font-semibold text-[#2C2C2C]">
                Prochaines réunions
              </h3>
              <p className="text-xs text-[#9A9A9A] mb-4">Réunions de la semaine</p>
              {semaineLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-14" />
                  ))}
                </div>
              ) : !(semaine?.reunions || []).length ? (
                <div className="text-center py-6">
                  <CalendarBlank size={32} className="mx-auto text-[#E0E0DB] mb-2" />
                  <p className="text-sm text-[#6B6B6B]">Aucune réunion prévue cette semaine</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {semaine.reunions.map((r) => (
                    <Link
                      key={r.id}
                      to={lienChantier(r.chantier_id, 'suivi', '&sub=reunions')}
                      className="flex items-center gap-3 p-3 bg-[#F5F5F3] border border-[#E0E0DB] hover:border-[#C75B39]"
                    >
                      <div className="w-12 text-center">
                        <p className="text-sm font-bold text-[#C75B39]">{dateCourte(r.date_debut)}</p>
                        <p className="text-xs text-[#6B6B6B]">{heureCourte(r.date_debut)}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{r.titre}</p>
                        <p className="text-xs text-[#6B6B6B] truncate">{r.chantier_name}{r.lieu ? ` · ${r.lieu}` : ''}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="card-kroquis">
              <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">
                Activité récente
              </h3>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              ) : stats?.recent_activities?.length === 0 ? (
                <p className="text-sm text-[#6B6B6B] text-center py-4">Aucune activité récente</p>
              ) : (
                <div className="space-y-0">
                  {stats?.recent_activities?.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="timeline-item">
                      <p className="text-sm text-[#2C2C2C]">
                        <span className="font-medium">{activity.user_name && activity.user_name !== 'Kroquis' ? activity.user_name : 'Enregistré automatiquement'}</span>
                      </p>
                      <p className="text-xs text-[#6B6B6B]">{activity.action}{activity.details ? ` — ${activity.details}` : ''}</p>
                      {/* P1#25 — sur quel chantier */}
                      {activity.chantier_name && (
                        <Link to={`/chantiers/${activity.chantier_id}`} className="text-xs text-[#C75B39] hover:underline block truncate">
                          {activity.chantier_name}
                        </Link>
                      )}
                      <p className="text-xs text-[#9A9A9A] mt-1">
                        {new Date(activity.timestamp).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
